import React, { useState, useEffect } from "react";
import Accordion from "react-bootstrap/Accordion";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./ProductFilter.css";
import { useFilter } from "../context/FilterContext";

import KeyIngredientIcon from "../images/key-ingredients.png";
import ProductClaimsIcon from "../images/product-claims.png";
import OtherIcon from "../images/other.png";
import FreeFromIcon from "../images/free-from.png";
import SkinTypesIcon from "../images/skin-types.png";
import SkinSensitiveIcon from "../images/skin-sensitive.png";
import SkinConcernsIcon from "../images/skin-concerns.png";
import { getProfileInfo } from "../api/auth";
export const filterParams = {
  "Skin Concerns": "sc",
  "Skin Types": "st",
  "Skin Sensitive": "ss",
  "Key Ingredients": "ki",
  "Product Claims": "pc",
  Other: "ot",
  "Free-form": "ff",
};

export const sortOptions = [
  { value: "", label: "Default" },
  { value: "popularity", label: "Most Popular" },
  { value: "rating", label: "Highest Rated" },
];

// const spFilterParams = ["ki", "pc", "ot", "ff"];

const filterIcons = {
  "Key Ingredients": KeyIngredientIcon,
  "Product Claims": ProductClaimsIcon,
  Other: OtherIcon,
  "Free-form": FreeFromIcon,
  "Skin Types": SkinTypesIcon,
  "Skin Sensitive": SkinSensitiveIcon,
  "Skin Concerns": SkinConcernsIcon,
};

export default function ProductFilter({
  items = {},
  id = "",
  accordionId,
  onFilterApplied,
  onFilterChange,
}) {
  const { filters, updateFilters } = useFilter();
  const [localFilters, setLocalFilters] = useState(filters);
  const [activeKey, setActiveKey] = useState(null);
  const [searchParams] = useSearchParams();

  const [matchMySkin, setMatchMySkin] = useState(false);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [matchError, setMatchError] = useState("");

  const navigate = useNavigate();
  const keyword = searchParams.get("q");
  const currentSort = searchParams.get("sort") || "";

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;

    setLocalFilters((prev) => {
      const values = new Set(prev[name] || []);
      if (checked) {
        values.add(value);
      } else {
        values.delete(value);
      }
      const updated = {
        ...prev,
        [name]: Array.from(values),
      };
      onFilterChange?.(updated); // optional real-time hook
      return updated;
    });
  };

  const handleSortChange = (e) => {
    const sortValue = e.target.value;
    const newParams = new URLSearchParams();
    if (keyword) newParams.set("q", keyword);
    if (sortValue) newParams.set("sort", sortValue);

    Object.entries(localFilters).forEach(([key, values]) => {
      values.forEach((value) => newParams.append(key, value));
    });

    navigate(`${window.location.pathname}?${newParams.toString()}`, {
      replace: true,
    });

    onFilterApplied?.(); // trigger product list reload
  };

  const getOptionsByLabel = (label) =>{
    const fromFilters = 
      (items?.Filters || []).find((p) => p?.name === label)?.sub_product_preference;
    return fromFilters ?? items?.[label] ?? [];
  };

  //Filter function to match individual skin
  const handleMatchMySkinToggle = async (e) => {
    const checked = e.target.checked;
    setMatchError("");
    setMatchMySkin(checked);

      if (!checked) {
        
        setLocalFilters((prev) => {
          const updated = { ...prev };
          delete updated.sc;
          delete updated.st;
          delete updated.ss;
          onFilterChange?.(updated);
          return updated;
        });
        return;
      }

      try {
        setLoadingMatch(true);
        const resp = await getProfileInfo();           
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Profile API failed: ${resp.status} ${txt || ""}`);
        }
        const profile = await resp.json();

        // Existing options (only check those that can be found on the page)）
        const scOptions = getOptionsByLabel("Skin Concerns");
        const stOptions = getOptionsByLabel("Skin Types");
        const ssOptions = getOptionsByLabel("Skin Sensitive");

        const scSet = new Set(scOptions.map((o) => String(o.id)));
        const stSet = new Set(stOptions.map((o) => String(o.id)));
        const ssSet = new Set(ssOptions.map((o) => String(o.id)));

        const scIds = (profile?.skin_concerns || [])
          .map((c) => String(c.id))
          .filter((id) => scSet.has(id));

        const stId =
          profile?.skin_type?.id && stSet.has(String(profile.skin_type.id))
            ? [String(profile.skin_type.id)]
            : [];

        const ssId =
          profile?.skin_sensitive_type?.id &&
          ssSet.has(String(profile.skin_sensitive_type.id))
            ? [String(profile.skin_sensitive_type.id)]
            : [];

        setLocalFilters((prev) => {
          const updated = { ...prev, sc: scIds, st: stId, ss: ssId };
          onFilterChange?.(updated);
          return updated;
        });

      } catch (err) {
        console.error(err);
        setMatchError(err?.message || "Failed to read the personal skin profile");
        setMatchMySkin(false);
      } finally {
        setLoadingMatch(false);
      }
  };


  const handleApply = (e) => {
    e.preventDefault();

    const newParams = new URLSearchParams();
    if (keyword) newParams.set("q", keyword);
    if (currentSort) newParams.set("sort", currentSort);

    Object.entries(localFilters).forEach(([key, values]) => {
      values.forEach((value) => newParams.append(key, value));
    });

    // Condensed 'sp' param
    // const spValues = spFilterParams.flatMap((key) => localFilters[key] || []);
    // if (spValues.length > 0) {
    //   newParams.set("sp", spValues.join(","));
    // }

    navigate(`${window.location.pathname}?${newParams.toString()}`, {
      replace: true,
    });

    updateFilters(localFilters);
    onFilterApplied?.(); // trigger product list reload
  };

  const handleClear = () => {
    setLocalFilters({});
    updateFilters({});

    const newParams = new URLSearchParams();
    if (keyword) newParams.set("q", keyword);
    if (currentSort) newParams.set("sort", currentSort);
    navigate(`${window.location.pathname}?${newParams.toString()}`, {
      replace: true,
    });

    onFilterApplied?.(); // reset product list
    setMatchMySkin(false);
  };

  const buildFilterListItems = (items, searchParam) => {
    const selectedValues = localFilters[searchParam] || [];

    return items.map((item, index) => (
      <div className="mb-2" key={`sub-item-${item.id}`}>
        <div className="form-check">
          <input
            name={searchParam}
            value={item.id}
            className="form-check-input filter-checkbox"
            type="checkbox"
            id={`${id}-${searchParam}-${index}-${item.id}`}
            checked={selectedValues.includes(String(item.id))}
            onChange={handleCheckboxChange}
          />
          <label
            className="form-check-label"
            htmlFor={`${id}-${searchParam}-${index}-${item.id}`}
          >
            {item.name}
          </label>
        </div>
      </div>
    ));
  };

  const buildFilterList = (name, items, index, searchParam) => {
    const icon = filterIcons[name];
    const key = `${searchParam}-${index}`;
    const count = localFilters[searchParam]?.length || 0;

    return (
      <Accordion.Item style={{ border: "none" }} eventKey={key} key={key}>
        <Accordion.Header>
          {icon && <img className="filter-icon" src={icon} alt="icon" />}
          <span style={{ flexGrow: 1 }}>{name}</span>
          {count > 0 && <span className="filter-count-badge">{count}</span>}
        </Accordion.Header>
        <Accordion.Body>
          {buildFilterListItems(items, searchParam)}
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  return (
    <form
      id={id || "filter-form"}
      className={`filter-container ${id}`}
      onSubmit={handleApply}
    >
      {/* Sort Dropdown */}
      <div className="mb-4">
        <label htmlFor="sort-select" className="form-label fw-semibold">
          Sort by
        </label>
        <select
          id="sort-select"
          className="form-select"
          value={currentSort}
          onChange={handleSortChange}
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {/* Match My Skin */}
      <div className="form-check mb-3 " style={{ marginLeft: '5px' }}>
        <input
          type="checkbox"
          className="form-check-input"
          id="match-my-skin"
          checked={matchMySkin}
          disabled={loadingMatch}
          onChange={handleMatchMySkinToggle}
        />
        <label className="form-check-label" htmlFor="match-my-skin">
          Match my skin
        </label>
        {loadingMatch && (
          <div className="form-text">Your skin profile is being read...</div>
        )}
        {matchError && (
          <div className="invalid-feedback d-block">{matchError}</div>
        )}
      </div>

      <Accordion
        id={accordionId}
        activeKey={activeKey}
        onSelect={(key) => setActiveKey(key === activeKey ? null : key)}
        alwaysOpen
      >
        {Object.keys(items).map((key, index) =>
          key === "Filters"
            ? items[key].map((pref, prefIndex) =>
                buildFilterList(
                  pref.name,
                  pref.sub_product_preference,
                  `${index}-${prefIndex}`,
                  filterParams[pref.name]
                )
              )
            : buildFilterList(key, items[key], index, filterParams[key])
        )}
      </Accordion>

      <div className="mt-4 text-center">
        <button className="btn btn-white" type="button" onClick={handleClear}>
          Clear
        </button>
        <button className="btn btn-primary text-white" type="submit">
          Apply
        </button>
      </div>
    </form>
  );
}
